import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CatHeading } from "blocks-nextjs/blocks-react/components/Heading/Heading";
import { CatLoadingIndicator } from "blocks-nextjs/blocks-react/components/LoadingIndicator/LoadingIndicator";
import React from "react";
import { BarChart } from "../Charts/BarChart";
const MS_PER_DAY = 86_400_000;
const SCROLL_DATE_THRESHOLD = 30;
const CHART_PX_PER_DATE = 50;
const CHART_MARGIN_BOTTOM = 30;
const CHART_MARGIN_TOP = 10;
const HOVER_MARKER_SIZE = 30;
const COLOR_CONNECTOR_2 = "#2563eb";
const NA_HOVER_TEMPLATE = "N/A<extra></extra>";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function fillMissingDates(data, startDate, endDate) {
    if (!data || !Array.isArray(data) || !startDate || !endDate)
        return [];
    // Build date list using pure date-string arithmetic.
    // API dates are date-only ("2026-03-25") so we avoid Date objects
    // to prevent any timezone shift — every user sees the same calendar day.
    const allDates = [];
    const [sy, sm, sd] = startDate.split("-").map(Number);
    const [ey, em, ed] = endDate.split("-").map(Number);
    const endMs = Date.UTC(ey, em - 1, ed);
    let curMs = Date.UTC(sy, sm - 1, sd);
    while (curMs <= endMs) {
        const d = new Date(curMs);
        allDates.push(d.toLocaleDateString("en-US", { day: "2-digit", month: "short", timeZone: "UTC" }));
        curMs += MS_PER_DAY;
    }
    return data.map((trace) => {
        if (!trace?.x || !trace?.y)
            return trace;
        const lookup = {};
        trace.x.forEach((date, i) => {
            lookup[date] = trace.y[i];
        });
        return {
            ...trace,
            hovertemplate: allDates.map((date) => (date in lookup ? trace.hovertemplate : NA_HOVER_TEMPLATE)),
            x: allDates,
            y: allDates.map((date) => lookup[date] ?? 0)
        };
    });
}
const LEGEND_ITEMS = [
    { color: "bg-[#1AC69E]", label: "Connector 1" },
    { color: "bg-[#2563eb]", label: "Connector 2" }
];
const Legend = () => (_jsxs("div", { className: "flex w-full items-center justify-center", "data-test": "energy-output-legend", children: [_jsx("span", { className: "text-md mr-2", children: "Legend:" }), _jsx("span", { className: "flex flex-wrap text-md", children: LEGEND_ITEMS.map((item, index) => (_jsxs("div", { className: "flex items-center mr-4 whitespace-nowrap", "data-test": `legend-item-${index}`, children: [_jsx("div", { className: `w-2.5 h-2.5 rounded-full mr-1 ${item.color}` }), _jsx("span", { children: item.label })] }, index))) })] }));
const EnergyOutputHistory = ({ dailyEnergyChartData, fromDate, isLoading = false, toDate, totalOutput }) => {
    const filledData = fillMissingDates(dailyEnergyChartData?.data, fromDate, toDate);
    // Use the filled date count (not original API count) for scroll decisions
    const dateCount = filledData[0]?.x?.length || 0;
    const shouldScroll = dateCount > SCROLL_DATE_THRESHOLD;
    const hoverTargets = (() => {
        if (!filledData.length || !dailyEnergyChartData?.data)
            return [];
        const originalDates = new Set();
        dailyEnergyChartData.data.forEach((trace) => {
            trace.x?.forEach((d) => originalDates.add(d));
        });
        const missingDates = filledData[0]?.x?.filter((d) => !originalDates.has(d)) || [];
        if (!missingDates.length)
            return [];
        return [
            {
                hoverlabel: { bgcolor: COLOR_CONNECTOR_2 },
                hovertemplate: NA_HOVER_TEMPLATE,
                marker: { color: "rgba(0,0,0,0)", opacity: 0, size: HOVER_MARKER_SIZE },
                mode: "markers",
                showlegend: false,
                type: "scatter",
                x: missingDates,
                y: missingDates.map(() => 0)
            }
        ];
    })();
    const modifiedChartData = dailyEnergyChartData
        ? {
            ...dailyEnergyChartData,
            data: [...filledData, ...hoverTargets],
            layout: {
                ...dailyEnergyChartData.layout,
                dragmode: false,
                margin: {
                    ...dailyEnergyChartData.layout.margin,
                    b: CHART_MARGIN_BOTTOM,
                    t: CHART_MARGIN_TOP
                },
                width: shouldScroll ? dateCount * CHART_PX_PER_DATE : dailyEnergyChartData.layout.width,
                xaxis: {
                    ...dailyEnergyChartData.layout.xaxis,
                    dtick: 1
                }
            }
        }
        : null;
    return (_jsx("div", { className: "border-solid border-2 rounded min-h-max p-4", "data-test": "energy-output-history-container", children: _jsxs("div", { className: "flex flex-col gap-3", "data-test": "asset-page-details-header-usage-history", children: [_jsx("ol", { className: "flex", children: _jsxs("li", { className: "mr-auto", children: [_jsxs("div", { className: "inline-block font-bold mb-4 pb-2", children: [_jsx(CatHeading, { "data-test": "asset-page-details-energy-usage-history", variant: "title", children: "Energy Output History" }), _jsx("hr", { className: "h-1 border-none bg-[#FFCD11] mt-3 w-1/2" })] }), _jsxs("div", { "data-test": "total-output", children: ["Total Output: ", _jsx("span", { className: "font-bold text-xl", children: totalOutput || "0 kWh" })] })] }) }), _jsx("div", { className: "block self-center w-full overflow-x-hidden", "data-test": "asset-page-details-barchart", id: "energy-use-history-chart-new", children: isLoading ? (_jsx("div", { className: "flex justify-center items-center h-full w-full min-h-[20vh]", "data-test": "energy-output-loading", children: _jsx(CatLoadingIndicator, {}) })) : dailyEnergyChartData ? (_jsxs(_Fragment, { children: [_jsx(Legend, {}), _jsx("div", { className: `w-full justify-center items-center ${shouldScroll ? "overflow-x-auto" : ""}`, "data-test": "energy-output-chart-container", children: _jsx(BarChart, { config: modifiedChartData.config, data: modifiedChartData.data, layout: modifiedChartData.layout }) })] })) : (_jsx("div", { className: "flex justify-center items-center h-full w-full min-h-[20vh]", "data-test": "energy-output-no-data", children: "No data" })) })] }) }));
};
export default EnergyOutputHistory;
